import { type ReactNode, createContext, useContext, useEffect } from "react";

export type Theme = "light" | "dark" | "high-contrast";

interface ThemeContextValue {
	theme: Theme;
	onThemeChange: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
	theme: Theme;
	onThemeChange: (theme: Theme) => void;
	children: ReactNode;
}

export function ThemeProvider({
	theme,
	onThemeChange,
	children,
}: ThemeProviderProps): ReactNode {
	useEffect(() => {
		if (typeof document === "undefined") {
			return;
		}
		document.documentElement.dataset.theme = theme;
	}, [theme]);

	return (
		<ThemeContext.Provider value={{ theme, onThemeChange }}>
			{children}
		</ThemeContext.Provider>
	);
}

export function useThemeContext(): ThemeContextValue {
	const ctx = useContext(ThemeContext);
	if (!ctx) {
		throw new Error("useThemeContext must be used within a ThemeProvider");
	}
	return ctx;
}
